import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { gsap } from 'gsap';
import './styles.css';
import CoverFlow from './CoverFlow';
import GridView from './GridView';
import SliderControl from './SliderControl';

// Security & Admin Components
import AuthProvider, { useAuth } from './contexts/AuthContext';
import AdminPanel from './components/AdminPanel';
import LoginModal from './components/LoginModal';

// Data Models
import { Card, CardCollection } from './models/Card';

const breakpoints = [
  { minScale: 0.5, maxScale: 0.65, columns: 33 },
  { minScale: 0.65, maxScale: 0.8, columns: 25 },
  { minScale: 0.8, maxScale: 1.0, columns: 17 },
  { minScale: 1.0, maxScale: 1.5, columns: 11 },
  { minScale: 1.5, maxScale: 2.0, columns: 7 },
  { minScale: 2.0, maxScale: 3.0, columns: 4 },
];

// Main App Component with Security
function SecureApp() {
  const { isAdmin } = useAuth();
  const gridContainerRef = useRef(null);

  const minScale = 0.5;
  const maxScale = 3.0;
  const [scaleFactor, setScaleFactor] = useState((minScale + maxScale) / 2);

  const baseCardWidth = 90;
  const baseCardHeight = 60;
  const gridGap = 5;

  const [visibleCols, setVisibleCols] = useState(0);
  const [visibleRows, setVisibleRows] = useState(0);
  const [centerX, setCenterX] = useState(0);
  const [centerY, setCenterY] = useState(0);

  const [currentX, setCurrentX] = useState(0);
  const [currentY, setCurrentY] = useState(0);
  const [logicalX, setLogicalX] = useState(0);
  const [logicalY, setLogicalY] = useState(0);

  // Removed currentIndex - no longer needed with position-based coverflow
  const [cellWidth, setCellWidth] = useState(0);
  const [cellHeight, setCellHeight] = useState(0);

  // Card collection state
  const [cardCollection, setCardCollection] = useState(new CardCollection());
  const [showLoginModal, setShowLoginModal] = useState(false);

  // This ref stores old logical coords before scaling
  const oldCoordsRef = useRef({ oldLogicalX: undefined, oldLogicalY: undefined });
  
  // Track whether change is from scale or resize to prevent unwanted jumps
  const isScaleChangeRef = useRef(false);

  const sliderHeight = 150;
  const sliderThumbHeight = 10;

  const getBreakpoint = useCallback((sf) => {
    return breakpoints.find(bp => sf >= bp.minScale && sf <= bp.maxScale) || breakpoints[breakpoints.length - 1];
  }, []);

  const calculateVisible = useCallback(() => {
    if (!gridContainerRef.current) return;
    const containerWidth = gridContainerRef.current.clientWidth;
    const containerHeight = gridContainerRef.current.clientHeight;
    const breakpoint = getBreakpoint(scaleFactor);
    const cols = breakpoint.columns;

    const totalGapWidth = gridGap * (cols - 1);
    const availableWidth = containerWidth - totalGapWidth;
    const computedCellWidth = availableWidth / cols;
    const computedCellHeight = (computedCellWidth / baseCardWidth) * baseCardHeight;

    document.documentElement.style.setProperty('--grid-cell-width', `${computedCellWidth}px`);
    document.documentElement.style.setProperty('--grid-cell-height', `${computedCellHeight}px`);
    document.documentElement.style.setProperty('--grid-gap', `${gridGap}px`);

    const fullCellWidth = computedCellWidth + gridGap;
    const fullCellHeight = computedCellHeight + gridGap;

    const rows = Math.floor(containerHeight / fullCellHeight);

    setVisibleCols(cols);
    setVisibleRows(rows);
    setCellWidth(fullCellWidth);
    setCellHeight(fullCellHeight);

    const newCenterX = Math.floor(cols / 2);
    const newCenterY = Math.floor(rows / 2);
    setCenterX(newCenterX);
    setCenterY(newCenterY);
  }, [scaleFactor, getBreakpoint, baseCardWidth, baseCardHeight, gridGap]);

  // Load saved collection on mount
  useEffect(() => {
    const savedData = localStorage.getItem('cardCollection');
    if (savedData) {
      try {
        const data = JSON.parse(savedData);
        const collection = CardCollection.fromJSON(data);
        setCardCollection(collection);
      } catch (error) {
        console.error('Failed to load saved collection:', error);
      }
    }
  }, []);

  // Get cards for coverflow based on current position
  const getCoverflowCards = useCallback(() => {
    const lx = logicalX + currentX - centerX;
    const ly = logicalY + currentY - centerY;
    
    const numItems = 5;
    const middleIndex = Math.floor(numItems / 2);
    const cards = [];
    
    // Get cards from left to right relative to current position
    for (let i = 0; i < numItems; i++) {
      const relativePosition = i - middleIndex;
      const cardX = lx + relativePosition;
      const cardY = ly;
      
      const card = cardCollection.getCardAt(cardX, cardY);
      cards.push({
        card,
        position: { x: cardX, y: cardY },
        relativePosition
      });
    }
    
    return cards;
  }, [logicalX, currentX, centerX, logicalY, currentY, cardCollection]);

  // Position tracking is now handled directly in getCoverflowCards

  const moveSelection = useCallback((dx, dy) => {
    const rightEdge = visibleCols - 1;
    const bottomEdge = visibleRows - 1;

    setCurrentX(prevX => {
      let newX = prevX;
      let newLogX = logicalX;
      if (dx > 0) {
        if (newX < rightEdge) {
          newX += 1;
        } else {
          newLogX += 1;
        }
      } else if (dx < 0) {
        if (newX > 0) {
          newX -= 1;
        } else {
          newLogX -= 1;
        }
      }
      setLogicalX(newLogX);
      return newX;
    });

    setCurrentY(prevY => {
      let newY = prevY;
      let newLogY = logicalY;
      if (dy > 0) {
        if (newY < bottomEdge) {
          newY += 1;
        } else {
          newLogY += 1;
        }
      } else if (dy < 0) {
        if (newY > 0) {
          newY -= 1;
        } else {
          newLogY -= 1;
        }
      }
      setLogicalY(newLogY);
      return newY;
    });
  }, [visibleCols, visibleRows, logicalX, logicalY]);

  useEffect(() => {
    const onKeyDown = (e) => {
      switch (e.key) {
        case 'ArrowLeft':
          moveSelection(-1, 0);
          break;
        case 'ArrowRight':
          moveSelection(1, 0);
          break;
        case 'ArrowUp':
          moveSelection(0, -1);
          break;
        case 'ArrowDown':
          moveSelection(0, 1);
          break;
        default:
          break;
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [moveSelection]);

  useEffect(() => {
    const onWheel = (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const delta = e.deltaY;
        const zoomStep = 0.05;
        setScaleFactor(prev => {
          if (delta < 0) return Math.min(maxScale, prev + zoomStep);
          return Math.max(minScale, prev - zoomStep);
        });
      }
    };
    const container = gridContainerRef.current;
    if (container) {
      container.addEventListener('wheel', onWheel, { passive: false });
    }
    return () => {
      if (container) container.removeEventListener('wheel', onWheel);
    };
  }, []);

  useEffect(() => {
    let resizeTimer;
    const onResize = () => {
      // Debounce resize to prevent excessive recalculations
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        // Mark this as a resize, NOT a scale change
        isScaleChangeRef.current = false;
        calculateVisible();
      }, 100); // 100ms debounce
    };
    window.addEventListener('resize', onResize);
    return () => {
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', onResize);
    };
  }, [calculateVisible]);

  // On initial mount, calculate once
  useEffect(() => {
    // Mark initial calculation as NOT a scale change
    isScaleChangeRef.current = false;
    calculateVisible();
    // Focus document body so arrow keys work immediately
    document.body.tabIndex = 0;
    document.body.focus();
  }, [calculateVisible]);

  // On scale factor change, mimic original logic:
  // Original steps:
  // 1) oldLogicalX = logicalX + currentX - centerX
  // 2) oldLogicalY = logicalY + currentY - centerY
  // 3) calculateVisible()
  // After DOM updates (in useLayoutEffect), we set:
  // 4) currentX = centerX
  // 5) currentY = centerY
  // 6) logicalX = oldLogicalX
  // 7) logicalY = oldLogicalY
  useEffect(() => {
    // Mark this as a scale change
    isScaleChangeRef.current = true;
    const oldLogicalX = logicalX + currentX - centerX;
    const oldLogicalY = logicalY + currentY - centerY;
    oldCoordsRef.current = { oldLogicalX, oldLogicalY };
    calculateVisible();
  }, [scaleFactor]);

  useLayoutEffect(() => {
    const { oldLogicalX, oldLogicalY } = oldCoordsRef.current;
    // Only run position preservation if:
    // 1) We have old coords from a scale change
    // 2) This was triggered by a scale change, NOT a window resize
    if (oldLogicalX !== undefined && oldLogicalY !== undefined && isScaleChangeRef.current) {
      // Adjust coords to preserve selection during zoom
      setCurrentX(centerX);
      setCurrentY(centerY);
      setLogicalX(oldLogicalX);
      setLogicalY(oldLogicalY);

      // Reset old coords and flag to avoid running this again unnecessarily
      oldCoordsRef.current = { oldLogicalX: undefined, oldLogicalY: undefined };
      isScaleChangeRef.current = false;
    }
  }, [centerX, centerY]);

  const handleScaleChange = useCallback((newScale) => {
    setScaleFactor(newScale);
  }, []);

  const handleGridItemClick = useCallback((col, row) => {
    setCurrentX(col);
    setCurrentY(row);
  }, []);

  const handleCoverFlowItemClick = useCallback((relativePosition) => {
    if (relativePosition !== 0) {
      moveSelection(relativePosition, 0);
    }
  }, [moveSelection]);

  // Admin handlers
  const handleImageUpload = useCallback(async (file) => {
    try {
      // Find next available position
      const lx = logicalX + currentX - centerX;
      const ly = logicalY + currentY - centerY;
      
      let newX = lx, newY = ly;
      while (cardCollection.getCardAt(newX, newY)) {
        newX++;
        if (newX > lx + 10) { // Wrap to next row after 10 attempts
          newX = lx;
          newY++;
        }
      }

      const card = await Card.fromFile(file, { x: newX, y: newY });
      const newCollection = new CardCollection();
      
      // Copy existing cards
      cardCollection.getAllCards().forEach(existingCard => {
        newCollection.addCard(existingCard);
      });
      
      // Add new card
      newCollection.addCard(card);
      
      setCardCollection(newCollection);
    } catch (error) {
      console.error('Failed to upload image:', error);
      throw error;
    }
  }, [cardCollection, logicalX, currentX, centerX, logicalY, currentY]);

  const handleSaveLayout = useCallback(() => {
    try {
      const data = cardCollection.toJSON();
      localStorage.setItem('cardCollection', JSON.stringify(data));
      alert('Layout saved successfully!');
    } catch (error) {
      console.error('Failed to save layout:', error);
      alert('Failed to save layout');
    }
  }, [cardCollection]);

  const handleAdminAccess = () => {
    setShowLoginModal(true);
  };

  return (
    <>
      <div id="coverflow">
        <CoverFlow
          coverflowCards={getCoverflowCards()}
          gsap={gsap}
          onItemClick={handleCoverFlowItemClick}
          scaleFactor={scaleFactor}
        />
      </div>
      <div id="grid-container" ref={gridContainerRef}>
        <GridView
          visibleCols={visibleCols}
          visibleRows={visibleRows}
          cellWidth={cellWidth}
          cellHeight={cellHeight}
          scaleFactor={scaleFactor}
          cardCollection={cardCollection}
          logicalX={logicalX}
          logicalY={logicalY}
          centerX={centerX}
          centerY={centerY}
          currentX={currentX}
          currentY={currentY}
          onItemClick={handleGridItemClick}
        />
        <SliderControl
          scaleFactor={scaleFactor}
          minScale={minScale}
          maxScale={maxScale}
          sliderHeight={sliderHeight}
          sliderThumbHeight={sliderThumbHeight}
          onScaleChange={handleScaleChange}
        />
      </div>

      {/* Admin Components */}
      {isAdmin && (
        <AdminPanel
          onImageUpload={handleImageUpload}
          cards={cardCollection.getAllCards()}
          onSaveLayout={handleSaveLayout}
        />
      )}

      {/* Admin access button for non-admins */}
      {!isAdmin && (
        <button 
          className="admin-access-btn"
          onClick={handleAdminAccess}
          title="Admin Access"
        >
          Admin
        </button>
      )}

      {/* Login Modal */}
      {showLoginModal && (
        <LoginModal onClose={() => setShowLoginModal(false)} />
      )}
    </>
  );
}

// Root App with Auth Provider
export default function App() {
  return (
    <AuthProvider>
      <SecureApp />
    </AuthProvider>
  );
}
